import { Box, FieldLabel, Flex, Loading, Select } from '@/components';
import { reportFileType } from '@/features/mold/models';
import { useFiscalYearOptions } from '@/features/search/_hooks/budget-types';
import { type FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { z } from 'zod';
import {
  ReportFileTypeLabelModifier,
  ReportTypeLabelModifier,
  reportType as reportTypeModel,
} from '../../projects/_models/report';
import { ReportInvestmentFundForm } from '../_components/ReportInvestmentFundForm';
import { ReportLocalGovernmentFundForm } from '../_components/ReportLocalGovernmentFundForm';
import { ReportOrganizingTableForm } from '../_components/ReportOrganizingTableForm';
import { ReportStandardFundForm } from '../_components/ReportStandardFundForm';

const reportTypeOptions = reportTypeModel.options.map((value) => {
  return {
    value,
    label: ReportTypeLabelModifier[value],
  };
});
const fileTypeOptions = reportFileType.options.map((value) => {
  return {
    value,
    label: ReportFileTypeLabelModifier[value],
  };
});

export const ReportProjectPage: FC = () => {
  const { t } = useTranslation();
  const [reportType, setReportType] =
    useState<z.infer<typeof reportTypeModel>>('project');
  const [fileType, setFileType] =
    useState<z.infer<typeof reportFileType>>('pdf');

  const fiscalYearOptions = useFiscalYearOptions();

  if (fiscalYearOptions.isPending || !fiscalYearOptions.data) {
    return <Loading />;
  }

  return (
    <Box marginTop={16}>
      <Flex vertical gap={24}>
        <Flex vertical gap={8}>
          <FieldLabel text={t('report-type')} required />
          <Select
            options={reportTypeOptions}
            value={reportType}
            onChange={setReportType}
            style={{
              width: '100%',
              maxWidth: 480,
            }}
          />
        </Flex>

        <Flex vertical gap={8}>
          <FieldLabel text={t('report-file-type')} required />
          <Select
            options={fileTypeOptions}
            value={fileType}
            onChange={setFileType}
            style={{
              width: '100%',
              maxWidth: 480,
            }}
          />
        </Flex>

        <Box>
          {reportType === 'project' && (
            <ReportOrganizingTableForm
              fileType={fileType}
              fiscalYearOptions={fiscalYearOptions.data}
            />
          )}
          {reportType === 'standard-fund' && (
            <ReportStandardFundForm
              fileType={fileType}
              fiscalYearOptions={fiscalYearOptions}
            />
          )}
          {reportType === 'local-government-fund' && (
            <ReportLocalGovernmentFundForm
              fiscalYearOptions={fiscalYearOptions}
              fileType={fileType}
            />
          )}
          {reportType === 'investment-fund' && (
            <ReportInvestmentFundForm
              fiscalYearOptions={fiscalYearOptions}
              fileType={fileType}
            />
          )}
        </Box>
      </Flex>
    </Box>
  );
};
